// RentalBiz API services
import { handleDemoApiRequest } from './demoApi'
import { isDemoMode } from './demoSession'

const API_BASE = '/api/rentalbiz'

const toSnake = (value) => {
  if (Array.isArray(value)) return value.map(toSnake)
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      const snakeKey = key
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/-/g, '_')
        .toLowerCase()
      acc[snakeKey] = toSnake(val)
      return acc
    }, {})
  }
  return value
}

const getList = (raw, keys = []) => {
  if (Array.isArray(raw)) return raw
  for (const key of keys) {
    if (Array.isArray(raw?.[key])) return raw[key]
    if (Array.isArray(raw?.data?.[key])) return raw.data[key]
  }
  if (Array.isArray(raw?.data)) return raw.data
  return []
}

const paginate = (items, page = 1, limit = 20) => {
  const currentPage = Number.parseInt(page, 10) || 1
  const perPage = Number.parseInt(limit, 10) || 20
  const total = items.length
  const pages = Math.max(1, Math.ceil(total / perPage))
  const start = (currentPage - 1) * perPage
  const paged = items.slice(start, start + perPage)
  return {
    items: paged,
    pagination: {
      page: currentPage,
      limit: perPage,
      total,
      pages
    }
  }
}

const request = async (url, options = {}) => {
  if (isDemoMode()) {
    const demoResponse = await handleDemoApiRequest(url, options)
    if (demoResponse !== null) {
      return { data: demoResponse }
    }
  }

  const response = await fetch(url, { credentials: 'include', ...options })
  let data = null
  try {
    data = await response.json()
  } catch {
    data = null
  }

  if (!response.ok) {
    const validationErrors = data?.errors && typeof data.errors === 'object'
      ? Object.values(data.errors).flat().filter(Boolean)
      : []
    const message = data?.message || validationErrors[0] || data?.title || `HTTP ${response.status}`
    const error = new Error(message)
    error.response = { status: response.status, data }
    throw error
  }

  return { data }
}

const parseFileName = (contentDisposition, fallback = 'documento.docx') => {
  if (!contentDisposition) return fallback

  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      return utf8Match[1]
    }
  }

  const asciiMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i)
  return asciiMatch?.[1] || fallback
}

const downloadRequest = async (url, options = {}, fallbackFileName = 'documento.docx') => {
  if (isDemoMode()) {
    const demoBlob = new Blob([`Demo de exportación: ${fallbackFileName}`], { type: 'text/plain;charset=utf-8' })
    const blobUrl = URL.createObjectURL(demoBlob)
    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = fallbackFileName.replace(/\.docx$/i, '.txt')
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(blobUrl)
    return { data: { fileName: anchor.download } }
  }

  const response = await fetch(url, { credentials: 'include', ...options })

  if (!response.ok) {
    let data = null
    try {
      data = await response.json()
    } catch {
      data = null
    }

    const validationErrors = data?.errors && typeof data.errors === 'object'
      ? Object.values(data.errors).flat().filter(Boolean)
      : []
    const message = data?.message || validationErrors[0] || data?.title || `HTTP ${response.status}`
    const error = new Error(message)
    error.response = { status: response.status, data }
    throw error
  }

  const blob = await response.blob()
  const fileName = parseFileName(response.headers.get('content-disposition'), fallbackFileName)
  const blobUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = blobUrl
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(blobUrl)

  return { data: { fileName } }
}

const asPayload = (data) => ({ data })

const toPaymentMethod = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return null
  const map = {
    cash: 'cash',
    bank_transfer: 'bankTransfer',
    banktransfer: 'bankTransfer',
    credit_card: 'creditCard',
    creditcard: 'creditCard',
    debit_card: 'debitCard',
    debitcard: 'debitCard',
    check: 'check',
    online: 'online',
    other: 'other'
  }
  return map[raw.toLowerCase()] || raw
}

const mapPaymentPayload = (data = {}) => {
  const payload = {}

  const contractId = data.contract_id || data.contractId
  if (contractId) payload.contractId = contractId

  if (data.type) payload.type = data.type

  if (data.amount != null && data.amount !== '') {
    payload.amount = Number(data.amount)
  }

  const dueDate = data.due_date || data.dueDate
  if (dueDate) payload.dueDate = dueDate

  if ('payment_method' in data || 'paymentMethod' in data) {
    payload.paymentMethod = toPaymentMethod(data.payment_method || data.paymentMethod)
  }

  if ('notes' in data) payload.notes = data.notes || null
  if ('status' in data && data.status) payload.status = data.status
  if ('payment_date' in data || 'paymentDate' in data) payload.paymentDate = data.payment_date || data.paymentDate || null
  if ('reference_number' in data || 'referenceNumber' in data) payload.referenceNumber = data.reference_number || data.referenceNumber || null
  if ('bank_reference' in data || 'bankReference' in data) payload.bankReference = data.bank_reference || data.bankReference || null
  if ('receipt_url' in data || 'receiptUrl' in data) payload.receiptUrl = data.receipt_url || data.receiptUrl || null

  if (data.penalty_amount != null && data.penalty_amount !== '') {
    payload.penaltyAmount = Number(data.penalty_amount)
  } else if (data.penaltyAmount != null && data.penaltyAmount !== '') {
    payload.penaltyAmount = Number(data.penaltyAmount)
  }

  if ('penalty_paid' in data || 'penaltyPaid' in data) payload.penaltyPaid = data.penalty_paid ?? data.penaltyPaid
  if ('is_automatic' in data || 'isAutomatic' in data) payload.isAutomatic = data.is_automatic ?? data.isAutomatic
  if ('reminder_sent' in data || 'reminderSent' in data) payload.reminderSent = data.reminder_sent ?? data.reminderSent
  if ('reminder_date' in data || 'reminderDate' in data) payload.reminderDate = data.reminder_date || data.reminderDate || null

  return payload
}

const mapPropertyPayload = (data = {}) => {
  const payload = {}

  if ('type' in data) payload.type = data.type
  if ('name' in data) payload.name = data.name
  if ('description' in data) payload.description = data.description || null
  if ('address' in data) payload.address = data.address
  if ('city' in data) payload.city = data.city
  if ('state' in data) payload.state = data.state
  if ('zip_code' in data || 'zipCode' in data) payload.zipCode = data.zip_code || data.zipCode || null
  if ('status' in data) payload.status = data.status

  if (data.monthly_rent != null && data.monthly_rent !== '') {
    payload.monthlyRent = Number(data.monthly_rent)
  } else if (data.monthlyRent != null && data.monthlyRent !== '') {
    payload.monthlyRent = Number(data.monthlyRent)
  }

  if (data.bedrooms != null && data.bedrooms !== '') {
    payload.bedrooms = Number.parseInt(data.bedrooms, 10)
  }

  if (data.bathrooms != null && data.bathrooms !== '') {
    payload.bathrooms = Number(data.bathrooms)
  }

  if (data.area_sqft != null && data.area_sqft !== '') {
    payload.areaSqft = Number(data.area_sqft)
  } else if (data.areaSqft != null && data.areaSqft !== '') {
    payload.areaSqft = Number(data.areaSqft)
  }

  return payload
}

const mapContractPayload = (data = {}) => {
  const payload = {}

  const propertyId = data.property_id || data.propertyId
  if (propertyId) payload.propertyId = propertyId

  const tenantId = data.tenant_id || data.tenantId
  if (tenantId) payload.tenantId = tenantId

  const startDate = data.start_date || data.startDate
  if (startDate) payload.startDate = startDate

  const endDate = data.end_date || data.endDate
  if (endDate) payload.endDate = endDate

  if ('contract_date' in data || 'contractDate' in data) {
    payload.contractDate = data.contract_date || data.contractDate || null
  }

  if (data.monthly_rent != null && data.monthly_rent !== '') {
    payload.monthlyRent = Number(data.monthly_rent)
  } else if (data.monthlyRent != null && data.monthlyRent !== '') {
    payload.monthlyRent = Number(data.monthlyRent)
  }

  if (data.security_deposit != null && data.security_deposit !== '') {
    payload.securityDeposit = Number(data.security_deposit)
  } else if (data.securityDeposit != null && data.securityDeposit !== '') {
    payload.securityDeposit = Number(data.securityDeposit)
  }

  if ('payment_frequency' in data || 'paymentFrequency' in data) {
    payload.paymentFrequency = data.payment_frequency || data.paymentFrequency
  }

  if (data.payment_day != null && data.payment_day !== '') {
    payload.paymentDay = Number(data.payment_day)
  } else if (data.paymentDay != null && data.paymentDay !== '') {
    payload.paymentDay = Number(data.paymentDay)
  }

  if (data.late_payment_penalty != null && data.late_payment_penalty !== '') {
    payload.latePaymentPenalty = Number(data.late_payment_penalty)
  } else if (data.latePaymentPenalty != null && data.latePaymentPenalty !== '') {
    payload.latePaymentPenalty = Number(data.latePaymentPenalty)
  }

  if ('terms_conditions' in data || 'termsConditions' in data) {
    payload.termsConditions = data.terms_conditions || data.termsConditions || null
  }

  if ('special_conditions' in data || 'specialConditions' in data) {
    payload.specialConditions = data.special_conditions || data.specialConditions || null
  }
  if ('nombres_apellidos_arrendador' in data || 'nombresApellidosArrendador' in data) {
    payload.nombresApellidosArrendador = data.nombres_apellidos_arrendador || data.nombresApellidosArrendador || null
  }

  if ('notes' in data) payload.notes = data.notes || null
  if ('documents' in data) payload.documents = data.documents || null
  if ('status' in data && data.status) payload.status = data.status
  if ('auto_renew' in data || 'autoRenew' in data) payload.autoRenew = data.auto_renew ?? data.autoRenew
  if ('renewal_notice_sent' in data || 'renewalNoticeSent' in data) {
    payload.renewalNoticeSent = data.renewal_notice_sent ?? data.renewalNoticeSent
  }

  if ('contract_domicile' in data || 'contractDomicile' in data) {
    payload.contractDomicile = data.contract_domicile || data.contractDomicile || null
  }
  if ('property_purpose' in data || 'propertyPurpose' in data) {
    payload.propertyPurpose = data.property_purpose || data.propertyPurpose || null
  }
  if ('increase_percentage' in data || 'increasePercentage' in data) {
    payload.increasePercentage = data.increase_percentage || data.increasePercentage || null
  }
  if ('property_installations' in data || 'propertyInstallations' in data) {
    payload.propertyInstallations = data.property_installations || data.propertyInstallations || null
  }
  if ('instalaciones_inmueble' in data || 'instalacionesInmueble' in data || 'propertyInstallations' in data) {
    payload.instalacionesInmueble = data.instalaciones_inmueble || data.instalacionesInmueble || data.propertyInstallations || null
  }
  if ('tenant_document_number' in data || 'tenantDocumentNumber' in data) {
    payload.tenantDocumentNumber = data.tenant_document_number || data.tenantDocumentNumber || null
  }
  if ('debtor_full_name' in data || 'debtorFullName' in data) {
    payload.debtorFullName = data.debtor_full_name || data.debtorFullName || null
  }
  if ('debtor_document_number' in data || 'debtorDocumentNumber' in data) {
    payload.debtorDocumentNumber = data.debtor_document_number || data.debtorDocumentNumber || null
  }
  if ('debtor_issue_place' in data || 'debtorIssuePlace' in data) {
    payload.debtorIssuePlace = data.debtor_issue_place || data.debtorIssuePlace || null
  }
  if ('debtor_address' in data || 'debtorAddress' in data) {
    payload.debtorAddress = data.debtor_address || data.debtorAddress || null
  }
  if ('debtor2_full_name' in data || 'debtor2FullName' in data) {
    payload.debtor2FullName = data.debtor2_full_name || data.debtor2FullName || null
  }
  if ('debtor2_document_number' in data || 'debtor2DocumentNumber' in data) {
    payload.debtor2DocumentNumber = data.debtor2_document_number || data.debtor2DocumentNumber || null
  }
  if ('debtor2_issue_place' in data || 'debtor2IssuePlace' in data) {
    payload.debtor2IssuePlace = data.debtor2_issue_place || data.debtor2IssuePlace || null
  }
  if ('debtor2_address' in data || 'debtor2Address' in data) {
    payload.debtor2Address = data.debtor2_address || data.debtor2Address || null
  }
  if ('landlord_notification_address' in data || 'landlordNotificationAddress' in data) {
    payload.landlordNotificationAddress = data.landlord_notification_address || data.landlordNotificationAddress || null
  }
  if ('signature_date' in data || 'signatureDate' in data) {
    payload.signatureDate = data.signature_date || data.signatureDate || null
  }

  return payload
}

export const propertyService = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params)
    const response = await request(`${API_BASE}/properties?${query.toString()}`)
    let items = toSnake(getList(response.data, ['properties']))

    if (params.search) {
      const search = params.search.toLowerCase()
      items = items.filter((p) =>
        (p.name || '').toLowerCase().includes(search) ||
        (p.address || '').toLowerCase().includes(search)
      )
    }
    if (params.type) items = items.filter((p) => p.type === params.type)
    if (params.status) items = items.filter((p) => p.status === params.status)
    if (params.city) items = items.filter((p) => (p.city || '').toLowerCase().includes(params.city.toLowerCase()))

    const { items: paged, pagination } = paginate(items, params.page, params.limit)
    return asPayload({ data: { properties: paged, pagination } })
  },

  getById: async (id) => {
    const response = await request(`${API_BASE}/properties/${id}`)
    return asPayload({ data: { property: toSnake(response.data) } })
  },

  create: async (data) => {
    const response = await request(`${API_BASE}/properties`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mapPropertyPayload(data))
    })
    return asPayload({ data: { property: toSnake(response.data) } })
  },

  update: async (id, data) => {
    const response = await request(`${API_BASE}/properties/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mapPropertyPayload(data))
    })
    return asPayload({ data: { property: toSnake(response.data) } })
  },

  delete: async (id) => {
    await request(`${API_BASE}/properties/${id}`, { method: 'DELETE' })
    return asPayload({ success: true })
  },

  getStats: async () => {
    const response = await request(`${API_BASE}/properties/stats`)
    return asPayload({ data: { stats: toSnake(response.data || {}) } })
  },

  validateInvitationCode: async (code) => {
    const response = await request(`${API_BASE}/properties/validate-invitation-code?code=${encodeURIComponent(code)}`)
    const payload = toSnake(response.data || {})
    return asPayload({ valid: true, data: { property: payload } })
  },

  generateInvitationCode: async (id) => {
    const response = await request(`${API_BASE}/properties/${id}/generate-code`, { method: 'POST' })
    const invitationCode = response.data?.invitationCode || response.data?.invitation_code
    return asPayload({ data: { invitation_code: invitationCode } })
  }
}

export const contractService = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params)
    const response = await request(`${API_BASE}/contracts?${query.toString()}`)
    const contracts = toSnake(getList(response.data, ['contracts']))
    const { items, pagination } = paginate(contracts, params.page, params.limit)
    return asPayload({ data: { contracts: items, pagination } })
  },

  getById: async (id) => {
    const response = await request(`${API_BASE}/contracts/${id}`)
    return asPayload({ data: { contract: toSnake(response.data) } })
  },

  create: async (data) => {
    const payload = mapContractPayload(data)
    const response = await request(`${API_BASE}/contracts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return asPayload({ data: { contract: toSnake(response.data) } })
  },

  update: async (id, data) => {
    const payload = mapContractPayload(data)
    const response = await request(`${API_BASE}/contracts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return asPayload({ data: { contract: toSnake(response.data) } })
  },

  delete: async (id) => {
    await request(`${API_BASE}/contracts/${id}`, { method: 'DELETE' })
    return asPayload({ success: true })
  },

  activate: async (id) => {
    const response = await request(`${API_BASE}/contracts/${id}/activate`, {
      method: 'POST'
    })
    return asPayload({ data: { contract: toSnake(response.data) } })
  },

  terminate: async (id, data = {}) => {
    const response = await request(`${API_BASE}/contracts/${id}/terminate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: data.termination_reason || '' })
    })
    return asPayload({ data: { contract: toSnake(response.data) } })
  },

  downloadWord: async (id, contractNumber = '') => {
    const fallbackFileName = contractNumber ? `Contrato-${contractNumber}.docx` : 'contrato.docx'
    await downloadRequest(`${API_BASE}/contracts/${id}/word`, {}, fallbackFileName)
    return asPayload({ success: true })
  }
}

export const paymentService = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params)
    const response = await request(`${API_BASE}/payments?${query.toString()}`)
    const payments = toSnake(getList(response.data, ['payments']))
    const { items, pagination } = paginate(payments, params.page, params.limit)
    return asPayload({ data: { payments: items, pagination } })
  },

  getById: async (id) => {
    const response = await request(`${API_BASE}/payments/${id}`)
    return asPayload({ data: { payment: toSnake(response.data) } })
  },

  create: async (data) => {
    const payload = mapPaymentPayload(data)
    const response = await request(`${API_BASE}/payments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return asPayload({ data: { payment: toSnake(response.data) } })
  },

  update: async (id, data) => {
    const payload = mapPaymentPayload(data)
    const response = await request(`${API_BASE}/payments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return asPayload({ data: { payment: toSnake(response.data) } })
  },

  delete: async (id) => {
    await request(`${API_BASE}/payments/${id}`, { method: 'DELETE' })
    return asPayload({ success: true })
  },

  markAsPaid: async (id, data = {}) => {
    const payload = {
      paymentDate: data.payment_date || data.paymentDate || null,
      paymentMethod: toPaymentMethod(data.payment_method || data.paymentMethod),
      receiptNumber: data.receipt_number || data.reference_number || data.receiptNumber || null,
      notes: data.notes || null
    }
    const response = await request(`${API_BASE}/payments/${id}/mark-paid`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return asPayload({ data: { payment: toSnake(response.data) } })
  },

  getPending: async () => {
    const response = await request(`${API_BASE}/payments/pending`)
    const payments = toSnake(getList(response.data, ['payments']))
    return asPayload({ data: { payments } })
  },

  getCertificateStatus: async ({ tenantId, year, month }) => {
    const query = new URLSearchParams({ tenantId, year, month })
    const response = await request(`${API_BASE}/payments/certificate-status?${query.toString()}`)
    return asPayload({ data: toSnake(response.data) })
  },

  sendCertificate: async ({ tenantId, year, month }) => {
    const response = await request(`${API_BASE}/payments/send-certificate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, year, month })
    })
    return asPayload({ data: toSnake(response.data) })
  }
}

export const notificationService = {
  getAll: async () => asPayload({ data: { notifications: [] } }),
  getDashboard: async () => asPayload({ data: { upcomingPayments: [], expiringContracts: [] } }),
  markAsRead: async () => asPayload({ success: true })
}

export const userService = {
  getAll: async () => asPayload({ data: [] }),
  getTenants: async (params = {}) => {
    const query = new URLSearchParams(params)
    const suffix = query.toString() ? `?${query.toString()}` : ''
    const response = await request(`/api/rentalbiz/tenants${suffix}`)
    return asPayload({ data: toSnake(response.data || []) })
  },
  getTenantsByPropertyCode: async (propertyId) => {
    const response = await request(`/api/rentalbiz/tenants/by-property/${propertyId}`)
    return asPayload({ data: toSnake(response.data || []) })
  },
  createTenant: async (payload) => {
    const response = await request('/api/rentalbiz/tenants', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return asPayload({ data: toSnake(response.data || {}) })
  },
  updateTenant: async (id, payload) => {
    const response = await request(`/api/rentalbiz/tenants/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return asPayload({ data: toSnake(response.data || {}) })
  },
  deactivateTenant: async (id) => {
    await request(`/api/rentalbiz/tenants/${id}`, { method: 'DELETE' })
    return asPayload({ success: true })
  },
  getById: async () => {
    const response = await request('/api/user/profile')
    return asPayload({ data: toSnake(response.data || {}) })
  },
  update: async (_id, data) => {
    const response = await request('/api/user/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: data.first_name,
        lastName: data.last_name,
        phone: data.phone,
        documentNumber: data.document_number,
        address: data.address
      })
    })
    return asPayload({ data: toSnake(response.data || {}) })
  },
  updateInvitationCode: async (code) => {
    const response = await request('/api/user/invitation-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code })
    })
    return asPayload({ data: toSnake(response.data || {}) })
  }
}

export const authService = {
  forgotPassword: async (email) => {
    const response = await request('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    })
    return asPayload({ data: toSnake(response.data || {}) })
  },

  resetPasswordWithToken: async ({ email, token, newPassword }) => {
    const response = await request('/api/auth/reset-password-with-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, token, newPassword })
    })
    return asPayload({ data: toSnake(response.data || {}) })
  },

  changePassword: async ({ currentPassword, newPassword }) => {
    const response = await request('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword })
    })
    return asPayload({ data: toSnake(response.data || {}) })
  }
}

export const paymentSchedulerService = {
  getScheduleStatus: async (contractId, year) => {
    const contractRes = await contractService.getById(contractId)
    const contract = contractRes?.data?.data?.contract || null
    const response = await request(`${API_BASE}/payments/contract/${contractId}`)
    const allPayments = toSnake(getList(response.data, ['payments']))
    const selectedYear = Number(year)

    const payments = allPayments.filter((p) => {
      const due = p?.due_date ? new Date(`${p.due_date}T12:00:00`) : null
      return due && due.getFullYear() === selectedYear
    })

    const monthKey = (dueDate) => {
      const due = new Date(`${dueDate}T12:00:00`)
      return `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, '0')}`
    }

    const summarizeType = (type) => {
      const typed = payments
        .filter((p) => p?.type === type)
        .sort((a, b) => new Date(`${a.due_date}T12:00:00`) - new Date(`${b.due_date}T12:00:00`))

      const existingMonths = new Set(typed.map((p) => monthKey(p.due_date)))
      const missingMonths = []
      for (let month = 1; month <= 12; month += 1) {
        const key = `${selectedYear}-${String(month).padStart(2, '0')}`
        if (!existingMonths.has(key)) missingMonths.push(key)
      }

      return {
        expected: 12,
        existing: existingMonths.size,
        missing: missingMonths.length,
        missingMonths,
        payments: typed
      }
    }

    const data = {
      contract_id: contractId,
      year: selectedYear,
      payment_day: contract?.payment_day || 1,
      types: {
        rent: summarizeType('rent'),
        electricity: summarizeType('electricity'),
        water: summarizeType('water'),
        gas: summarizeType('gas')
      }
    }

    return asPayload({ data })
  },

  preview: async ({ contract_id, type, year, payment_day }) => {
    const contractRes = await contractService.getById(contract_id)
    const contract = contractRes?.data?.data?.contract
    if (!contract) {
      const error = new Error('Contrato no encontrado')
      error.response = { status: 404, data: { message: 'Contrato no encontrado' } }
      throw error
    }

    const selectedYear = Number(year)
    const paymentDay = Number(payment_day || contract.payment_day || 1)
    const response = await request(`${API_BASE}/payments/contract/${contract_id}`)
    const allPayments = toSnake(getList(response.data, ['payments']))
    const typedYearPayments = allPayments.filter((p) => {
      if (p?.type !== type || !p?.due_date) return false
      const due = new Date(`${p.due_date}T12:00:00`)
      return due.getFullYear() === selectedYear
    })

    const existingMonths = new Set(
      typedYearPayments.map((p) => {
        const due = new Date(`${p.due_date}T12:00:00`)
        return due.getMonth() + 1
      })
    )

    const maxDayForMonth = (y, m) => new Date(y, m, 0).getDate()
    const buildDueDate = (y, m, d) => {
      const day = Math.min(Math.max(1, d), maxDayForMonth(y, m))
      return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }

    const monthlyAmount = type === 'rent'
      ? Number(contract.monthly_rent || 0)
      : Number(typedYearPayments[0]?.amount || 0)

    const payments_to_create = []
    for (let month = 1; month <= 12; month += 1) {
      if (existingMonths.has(month)) continue
      payments_to_create.push({
        period: new Date(selectedYear, month - 1, 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }),
        due_date: buildDueDate(selectedYear, month, paymentDay),
        amount: monthlyAmount
      })
    }

    return asPayload({
      data: {
        contract_id,
        type,
        year: selectedYear,
        payment_day: paymentDay,
        payments_to_create,
        will_create: payments_to_create.length,
        will_skip: 12 - payments_to_create.length
      }
    })
  },

  scheduleRent: async ({ contract_id, year, payment_day }) => {
    const previewRes = await paymentSchedulerService.preview({
      contract_id,
      type: 'rent',
      year,
      payment_day
    })
    const preview = previewRes?.data?.data || {}
    const toCreate = preview.payments_to_create || []

    let scheduled = 0
    for (const item of toCreate) {
      await paymentService.create({
        contract_id,
        type: 'rent',
        amount: Number(item.amount || 0),
        due_date: item.due_date,
        notes: `Programado automaticamente para ${item.period}`
      })
      scheduled += 1
    }

    return asPayload({
      data: {
        scheduled,
        skipped: 12 - scheduled
      }
    })
  },

  rescheduleUtility: async ({ contract_id, utility_type, year, payment_day, amount }) => {
    const selectedYear = Number(year)
    const response = await request(`${API_BASE}/payments/contract/${contract_id}`)
    const allPayments = toSnake(getList(response.data, ['payments']))
    const utilityPayments = allPayments
      .filter((p) => {
        if (p?.type !== utility_type || !p?.due_date) return false
        const due = new Date(`${p.due_date}T12:00:00`)
        return due.getFullYear() === selectedYear
      })
      .sort((a, b) => new Date(`${a.due_date}T12:00:00`) - new Date(`${b.due_date}T12:00:00`))

    const isProtected = (status) => ['paid', 'approved'].includes(String(status || '').toLowerCase())
    const maxDayForMonth = (y, m) => new Date(y, m, 0).getDate()
    const buildDueDate = (y, m, d) => {
      const day = Math.min(Math.max(1, Number(d || 1)), maxDayForMonth(y, m))
      return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }

    const byMonth = new Map()
    for (const payment of utilityPayments) {
      const due = new Date(`${payment.due_date}T12:00:00`)
      const month = due.getMonth() + 1
      if (!byMonth.has(month)) byMonth.set(month, [])
      byMonth.get(month).push(payment)
    }

    let created = 0
    let deleted = 0
    let copies = 0

    for (let month = 1; month <= 12; month += 1) {
      const targetDueDate = buildDueDate(selectedYear, month, payment_day)
      const monthPayments = byMonth.get(month) || []
      const preserved = monthPayments.filter((p) => isProtected(p.status))
      const editable = monthPayments.filter((p) => !isProtected(p.status))

      if (preserved.length > 0) {
        copies += preserved.length
      }

      if (preserved.length === 0 && editable.length === 0) {
        await paymentService.create({
          contract_id,
          type: utility_type,
          amount: Number(amount || 0),
          due_date: targetDueDate,
          notes: `Programado automaticamente (${utility_type})`
        })
        created += 1
        continue
      }

      if (editable.length > 0) {
        const [first, ...rest] = editable
        await paymentService.update(first.id, {
          type: utility_type,
          amount: Number(amount || first.amount || 0),
          due_date: targetDueDate,
          status: 'pending'
        })
        for (const extra of rest) {
          await paymentService.update(extra.id, {
            status: 'cancelled',
            notes: 'Cancelado por reprogramacion anual'
          })
          deleted += 1
        }
      }
    }

    return asPayload({
      data: {
        copies,
        deleted,
        created
      }
    })
  }
}

export const auditLogService = {
  getAll: async () => asPayload({ data: { logs: [], pagination: { page: 1, limit: 20, total: 0, pages: 1 } } })
}

const APPOINTMENTS_API_BASE = '/api/appointments'
const RENTALTECH_API_BASE = '/api/rentaltech'

export const agendaLabService = {
  getServices: async () => asPayload({ data: (await request(`${APPOINTMENTS_API_BASE}/services`)).data }),
  createService: async (payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  updateService: async (id, payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/services/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  deleteService: async (id) => {
    await request(`${APPOINTMENTS_API_BASE}/services/${id}`, { method: 'DELETE' })
    return asPayload({ success: true })
  },
  getSchedule: async () => asPayload({ data: (await request(`${APPOINTMENTS_API_BASE}/schedule`)).data }),
  saveSchedule: async (payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/schedule`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  regenerateSchedule: async () => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/schedule/regenerate`, {
      method: 'POST'
    })).data
  }),
  deleteSchedule: async () => {
    await request(`${APPOINTMENTS_API_BASE}/schedule`, { method: 'DELETE' })
    return asPayload({ success: true })
  },
  getPublicBookingSettings: async () => asPayload({ data: (await request(`${APPOINTMENTS_API_BASE}/public-booking-settings`)).data }),
  savePublicBookingSettings: async (payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/public-booking-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  getClients: async () => asPayload({ data: (await request(`${APPOINTMENTS_API_BASE}/clients`)).data }),
  createClient: async (payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  updateClient: async (id, payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/clients/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  deleteClient: async (id) => {
    await request(`${APPOINTMENTS_API_BASE}/clients/${id}`, { method: 'DELETE' })
    return asPayload({ success: true })
  },
  regenerateInvitation: async (id) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/clients/${id}/invitation`, { method: 'POST' })).data
  }),
  getCalendar: async (from, to) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)).data
  }),
  getAvailability: async (serviceId, from, to) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/availability?serviceId=${serviceId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)).data
  }),
  createAppointment: async (payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  rescheduleAppointment: async (id, payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/${id}/reschedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  cancelAppointment: async (id, payload) => asPayload({
    data: (await request(`${APPOINTMENTS_API_BASE}/${id}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data
  }),
  getWorkload: async (date) => asPayload({ data: (await request(`${APPOINTMENTS_API_BASE}/workload?date=${date}`)).data })
}

export const publicAgendaService = {
  getAgenda: async (ownerUserId) => asPayload({ data: (await request(`/public/agenda/${ownerUserId}`, { credentials: 'omit' })).data }),
  validateInvitation: async (ownerUserId, invitationCode) => asPayload({
    data: (await request(`/public/agenda/${ownerUserId}/validate-invitation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invitationCode }),
      credentials: 'omit'
    })).data
  }),
  getAvailability: async (ownerUserId, serviceId, from, to) => asPayload({
    data: (await request(`/public/agenda/${ownerUserId}/availability?serviceId=${serviceId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, {
      credentials: 'omit'
    })).data
  }),
  reserve: async (ownerUserId, payload) => asPayload({
    data: (await request(`/public/agenda/${ownerUserId}/reservations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit'
    })).data
  })
}

export const rentalTechService = {
  getCustomers: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/customers`)).data || []) }),
  getCustomerById: async (id) => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/customers/${id}`)).data || {}) }),
  saveCustomer: async (payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || {})
  }),
  deleteCustomer: async (id) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/customers/${id}`, {
      method: 'DELETE'
    })).data || {})
  }),
  getDashboard: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/dashboard`)).data || {}) }),
  getEquipments: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/equipments`)).data || []) }),
  saveEquipment: async (payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/equipments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || {})
  }),
  deleteEquipment: async (id) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/equipments/${id}`, {
      method: 'DELETE'
    })).data || {})
  }),
  getEquipmentById: async (id) => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/equipments/${id}`)).data || {}) }),
  getEquipmentLifeSheet: async (id) => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/equipments/${id}/life-sheet`)).data || null) }),
  saveEquipmentLifeSheet: async (id, payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/equipments/${id}/life-sheet`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || {})
  }),
  getOrders: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/rental-orders`)).data || []) }),
  validateAvailability: async (payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/rental-orders/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || {})
  }),
  createOrder: async (payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/rental-orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || {})
  }),
  updateOrder: async (id, payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/rental-orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || {})
  }),
  getContractPdfUrl: (id) => (
    isDemoMode()
      ? `data:text/plain;charset=utf-8,${encodeURIComponent(`Contrato demo ${id}`)}`
      : `${RENTALTECH_API_BASE}/rental-orders/${id}/contract-pdf`
  ),
  signOrder: async (id, signerName) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/rental-orders/${id}/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signerName })
    })).data || {})
  }),
  getAvailability: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/availability`)).data || []) }),
  getMaintenance: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/maintenance-orders`)).data || []) }),
  autoMaintenance: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/maintenance-orders/auto-generate`, { method: 'POST' })).data || []) }),
  getTelemetry: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/telemetry/latest`)).data || []) }),
  simulateTelemetry: async (payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/telemetry/simulate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || {})
  }),
  createInspection: async (payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/inspections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || {})
  }),
  getInspections: async () => asPayload({ data: toSnake((await request(`${RENTALTECH_API_BASE}/inspections`)).data || []) }),
  getInspectionPdfUrl: (id) => (
    isDemoMode()
      ? `data:text/plain;charset=utf-8,${encodeURIComponent(`Acta demo ${id}`)}`
      : `${RENTALTECH_API_BASE}/inspections/${id}/report-pdf`
  ),
  closeBilling: async (payload) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/billing/close-period`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })).data || [])
  }),
  failureScore: async (equipmentId) => asPayload({
    data: toSnake((await request(`${RENTALTECH_API_BASE}/predictive/failure-score/${equipmentId}`)).data || {})
  })
}

export const telegramIntegrationService = {
  getStatus: async () => asPayload({
    data: (await request('/api/integrations/telegram/status')).data
  }),
  connect: async (chatId, isEnabled = true) => asPayload({
    data: (await request('/api/integrations/telegram/connect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chatId, isEnabled })
    })).data
  }),
  test: async ({ title, message } = {}) => asPayload({
    data: (await request('/api/integrations/telegram/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, message })
    })).data
  })
}

export const rentalBizTelegramService = {
  getStatus: async () => asPayload({
    data: (await request(`${API_BASE}/telegram/status`)).data
  }),
  connect: async (channelId, isEnabled = true) => asPayload({
    data: (await request(`${API_BASE}/telegram/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelId, isEnabled })
    })).data
  }),
  test: async () => asPayload({
    data: (await request(`${API_BASE}/telegram/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })).data
  })
}
